import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    FileText, Building2, Users, Loader2,
    RotateCcw, Send, ChevronDown, CheckCircle,
    Navigation, Eye, EyeOff, IndianRupee, Wallet,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';
import { formatIndianCurrency, getAmountDisplay } from '../../utilities/amountToTextHelper';

import {
    fetchAllCostCentersClient,
    fetchAllClients,
    fetchSubClientsByClientID,
    fetchCCDetailsByCcCode,
    saveNewClientPO,
    clearSubClients,
    clearCCDetails,
    clearSaveResult,
    resetClientPOCreation,
    selectCostCentersArray,
    selectCostCentersLoading,
    selectClientsArray,
    selectClientsLoading,
    selectSubClientsArray,
    selectSubClientsLoading,
    selectCCDetails,
    selectCCDetailsLoading,
    selectSaveStatus,
    selectSaveLoading,
    selectSaveError,
} from '../../slices/accountsSlice/clientPOCreationSlice';

// ── Constants ─────────────────────────────────────────────────────────────────

const BG_APPLICABLE_OPTIONS = ['Yes', 'No'];
const MOB_ADVANCE_OPTIONS = [
    { id: '1', label: 'Yes' },
    { id: '2', label: 'No' },
];

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDateForAPI = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) return val;
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
        const [yyyy, mm, dd] = val.split('T')[0].split('-');
        return `${dd}-${MONTH_ABBR[parseInt(mm, 10) - 1]}-${yyyy}`;
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

// ── Shared UI ─────────────────────────────────────────────────────────────────

const inputCls =
    'w-full px-3.5 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-300 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectIcon = ({ loading }) => (
    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
        {loading
            ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle, action }) => (
    <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40 rounded-t-2xl">
        <div className="flex items-center gap-3">
            <Icon className="h-4 w-4 text-indigo-500" />
            <div>
                <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
                {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500">{subtitle}</p>}
            </div>
        </div>
        {action}
    </div>
);

const InnerHeader = ({ icon: Icon, title, subtitle }) => (
    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100 dark:border-gray-700">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-md">
            <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
            <h3 className="text-base font-bold text-gray-800 dark:text-gray-100">{title}</h3>
            {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
    </div>
);

// ── Component ─────────────────────────────────────────────────────────────────

const AddNewClientPO = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);

    const roleId = userData?.roleId || userData?.RID || 0;
    const userName = userData?.userName || userData?.UserName || 'system';

    // ── Redux ──────────────────────────────────────────────────────────────
    const costCenters = useSelector(selectCostCentersArray);
    const ccLoading = useSelector(selectCostCentersLoading);
    const clients = useSelector(selectClientsArray);
    const clientsLoading = useSelector(selectClientsLoading);
    const subClients = useSelector(selectSubClientsArray);
    const subClientsLoading = useSelector(selectSubClientsLoading);
    const ccDetails = useSelector(selectCCDetails);
    const ccDetailsLoading = useSelector(selectCCDetailsLoading);
    const saveStatus = useSelector(selectSaveStatus);
    const saveLoading = useSelector(selectSaveLoading);
    const saveError = useSelector(selectSaveError);

    // ── Form state ────────────────────────────────────────────────────────
    const [costCenter, setCostCenter] = useState('');
    const [showCCDetails, setShowCCDetails] = useState(false);
    const [clientId, setClientId] = useState('');
    const [subClientId, setSubClientId] = useState('');
    const [pono, setPono] = useState('');
    const [poStartDate, setPoStartDate] = useState(new Date());
    const [poCompletionDate, setPoCompletionDate] = useState(new Date());
    const [bgApplicable, setBgApplicable] = useState('');
    const [poValue, setPoValue] = useState('');
    const [gst, setGst] = useState('');
    const [mobAdvance, setMobAdvance] = useState('');
    const [raBill, setRaBill] = useState('');
    const [raBillDues, setRaBillDues] = useState('');
    const [advSettle, setAdvSettle] = useState('');
    const [userRemarks, setUserRemarks] = useState('');

    // ── Init ──────────────────────────────────────────────────────────────
    useEffect(() => {
        dispatch(fetchAllCostCentersClient());
        dispatch(fetchAllClients());
        return () => {
            dispatch(resetClientPOCreation());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // Fetch sub-clients on client change
    useEffect(() => {
        setSubClientId('');
        dispatch(clearSubClients());
        if (clientId) {
            dispatch(fetchSubClientsByClientID(clientId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    // Save result effect
    useEffect(() => {
        if (saveStatus === 'success') {
            toast.success('PO Details Added Successfully.');
            dispatch(clearSaveResult());
            handleFullReset();
        } else if (saveStatus === 'failed' && saveError) {
            toast.error(saveError);
            dispatch(clearSaveResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveStatus]);

    // ── Handlers ──────────────────────────────────────────────────────────
    const handleFullReset = () => {
        setCostCenter(''); setShowCCDetails(false);
        setClientId(''); setSubClientId('');
        setPono(''); setPoStartDate(new Date()); setPoCompletionDate(new Date());
        setBgApplicable(''); setPoValue(''); setGst('');
        setMobAdvance(''); setRaBill(''); setRaBillDues(''); setAdvSettle('');
        setUserRemarks('');
        dispatch(resetClientPOCreation());
        dispatch(fetchAllCostCentersClient());
        dispatch(fetchAllClients());
    };

    const toggleCCDetails = () => {
        if (!costCenter) { toast.warn('Select Cost Center.'); return; }
        if (showCCDetails) {
            setShowCCDetails(false);
            dispatch(clearCCDetails());
        } else {
            dispatch(fetchCCDetailsByCcCode(costCenter));
            setShowCCDetails(true);
        }
    };

    const handleNumericChange = (setter) => (e) => {
        const val = e.target.value;
        if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) setter(val);
    };

    const total = (parseFloat(poValue) || 0) + (parseFloat(gst) || 0);

    const handleSubmit = () => {
        if (!costCenter) { toast.warn('Select Cost Center.'); return; }
        if (!clientId) { toast.warn('Select Client.'); return; }
        if (!subClientId) { toast.warn('Select Sub-Client.'); return; }
        if (!pono.trim()) { toast.warn('Enter PO No.'); return; }
        if (!poStartDate) { toast.warn('Select Start Date.'); return; }
        if (!poCompletionDate) { toast.warn('Select Completion Date.'); return; }
        if (!bgApplicable) { toast.warn('Select Is BG Applicable.'); return; }
        if (!poValue || parseFloat(poValue) <= 0) { toast.warn('Enter PO Value.'); return; }
        if (total <= 0) { toast.warn('Total Value cannot be Zero.'); return; }
        if (!mobAdvance) { toast.warn('Select Mobilisation Advance.'); return; }
        if (!raBill.trim()) { toast.warn('Enter RA Bill Value.'); return; }
        if (mobAdvance === '1' && !advSettle.trim()) { toast.warn('Enter Advance Settlement.'); return; }
        if (!userRemarks.trim()) { toast.warn('Enter User Remarks.'); return; }

        dispatch(saveNewClientPO({
            pono: pono.replace(/&/g, 'and'),
            postartdate: formatDateForAPI(poStartDate),
            pocompletiondate: formatDateForAPI(poCompletionDate),
            total: total,
            povalue: parseFloat(poValue) || 0,
            gst: parseFloat(gst) || 0,
            clientid: clientId,
            subclientid: subClientId,
            madvance: mobAdvance === '1',
            rabill: raBill,
            rabilldues: raBillDues,
            advancesettelment: advSettle,
            costCenter,
            userRemarks,
            bgApplicable,
            roleId,
            createdBy: userName,
        }));
    };

    // ── Derived ───────────────────────────────────────────────────────────
    const isBusy = saveLoading || ccLoading || clientsLoading;
    const selectedCCName = costCenters.find((c) => c.CC_Code === costCenter)?.CC_Name || '—';
    const selectedClientName = clients.find((c) => c.ClientCode === clientId)?.ClientName || '—';
    const selectedSubClientName = subClients.find((c) => c.SubClientCode === subClientId)?.SubClientCodename || '—';

    const showSummary = costCenter && clientId && subClientId && pono.trim() &&
        poStartDate && poCompletionDate && bgApplicable && poValue && total > 0 &&
        mobAdvance && raBill.trim() && (mobAdvance !== '1' || advSettle.trim()) && userRemarks.trim();

    const canSubmit = showSummary && !saveLoading;

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            {/* ── Page Header ──────────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-7 text-white">
                    <div className="absolute inset-0 opacity-10"
                        style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
                    <div className="absolute top-0 right-0 w-72 h-72 bg-purple-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20">
                                <FileText className="h-7 w-7 text-white" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">Accounts Module</span>
                                </div>
                                <h1 className="text-2xl md:text-3xl font-black tracking-tight">
                                    {menuData?.name || 'Purchase Order Information'}
                                </h1>
                                <p className="text-indigo-200 text-sm mt-0.5">Create a new client purchase order</p>
                            </div>
                        </div>
                        <div className="hidden sm:flex items-center gap-3 text-indigo-200">
                            <div className="text-right">
                                <p className="text-xs uppercase tracking-wider">Module</p>
                                <p className="text-sm font-bold text-white">Accounts / Client PO</p>
                            </div>
                            <Navigation className="h-5 w-5 opacity-60" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto space-y-6">

                {/* ── Section 1: Cost Center ────────────────────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <SectionHeader
                        icon={Building2}
                        title="Cost Center"
                        subtitle="Select the cost center this PO belongs to"
                        action={
                            <button
                                onClick={handleFullReset}
                                disabled={isBusy}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                            >
                                <RotateCcw className="h-3.5 w-3.5" /> Reset
                            </button>
                        }
                    />
                    <div className="p-6 md:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                            <div className="md:col-span-2">
                                <Label required>Cost Center</Label>
                                <div className="relative">
                                    <select
                                        className={`${inputCls} appearance-none pr-10`}
                                        value={costCenter}
                                        onChange={(e) => { setCostCenter(e.target.value); setShowCCDetails(false); dispatch(clearCCDetails()); }}
                                        disabled={ccLoading || isBusy}
                                    >
                                        <option value="">{ccLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {costCenters.map((c) => (
                                            <option key={c.CC_Code} value={c.CC_Code}>{c.CC_Code} — {c.CC_Name}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={ccLoading} />
                                </div>
                            </div>
                            <div>
                                <button
                                    onClick={toggleCCDetails}
                                    disabled={!costCenter || isBusy}
                                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    {ccDetailsLoading
                                        ? <><Loader2 className="h-4 w-4 animate-spin" /> Loading…</>
                                        : showCCDetails
                                            ? <><EyeOff className="h-4 w-4" /> Close Details</>
                                            : <><Eye className="h-4 w-4" /> View Details</>}
                                </button>
                            </div>
                        </div>

                        {showCCDetails && ccDetails && (
                            <div className="mt-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30 p-5">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">Cost Center Details</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    {[
                                        { label: 'CC Code', value: ccDetails.CC_Code },
                                        { label: 'CC Name', value: ccDetails.CC_Name },
                                        { label: 'CC Type', value: ccDetails.CC_Type },
                                        { label: 'CC Sub Type', value: ccDetails.CC_SubType },
                                        { label: 'Final Offer No', value: ccDetails.Final_Offerno },
                                        { label: 'Final Offer Date', value: ccDetails.Final_Offerdate ? formatDateForAPI(ccDetails.Final_Offerdate) : '—' },
                                        { label: 'Ref No', value: ccDetails.Ref_No },
                                        { label: 'Ref Date', value: ccDetails.Ref_Date ? formatDateForAPI(ccDetails.Ref_Date) : '—' },
                                        { label: 'Phone No', value: ccDetails.CC_Phoneno },
                                        { label: 'Incharge Name', value: ccDetails.CC_Inchargename },
                                        { label: 'Incharge Phone', value: ccDetails.CC_Incharge_Phno },
                                        { label: 'Address', value: ccDetails.Address, wide: true },
                                    ].map(({ label, value, wide }) => (
                                        <div key={label} className={wide ? 'md:col-span-4' : ''}>
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                                            <p className="font-semibold text-gray-800 dark:text-gray-100 break-words">{value || '—'}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Section 2: PO Information ────────────────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <SectionHeader
                        icon={FileText}
                        title="Purchase Order Information"
                        subtitle="Client, sub-client, PO number and dates"
                    />
                    <div className="p-6 md:p-8">
                        <InnerHeader icon={Users} title="Client Details" />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                            <div>
                                <Label required>Client</Label>
                                <div className="relative">
                                    <select
                                        className={`${inputCls} appearance-none pr-10`}
                                        value={clientId}
                                        onChange={(e) => setClientId(e.target.value)}
                                        disabled={clientsLoading || isBusy}
                                    >
                                        <option value="">{clientsLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {clients.map((c) => (
                                            <option key={c.ClientCode} value={c.ClientCode}>{c.ClientName}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={clientsLoading} />
                                </div>
                            </div>
                            <div>
                                <Label required>Sub-Client</Label>
                                <div className="relative">
                                    <select
                                        className={`${inputCls} appearance-none pr-10`}
                                        value={subClientId}
                                        onChange={(e) => setSubClientId(e.target.value)}
                                        disabled={!clientId || subClientsLoading || isBusy}
                                    >
                                        <option value="">{!clientId ? 'Select client first' : subClientsLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {subClients.map((c) => (
                                            <option key={c.SubClientCode} value={c.SubClientCode}>{c.SubClientCodename}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={subClientsLoading} />
                                </div>
                            </div>
                            <div>
                                <Label required>PO No</Label>
                                <input
                                    type="text"
                                    className={inputCls}
                                    placeholder="Enter PO number"
                                    value={pono}
                                    onChange={(e) => setPono(e.target.value)}
                                    disabled={isBusy}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <Label required>PO Start Date</Label>
                                <CustomDatePicker
                                    value={poStartDate}
                                    onChange={(d) => {
                                        setPoStartDate(d);
                                        if (d && poCompletionDate && new Date(d) > new Date(poCompletionDate)) {
                                            setPoCompletionDate(d);
                                        }
                                    }}
                                    disabled={isBusy}
                                    maxDate={new Date()}
                                />
                            </div>
                            <div>
                                <Label required>PO Completion Date</Label>
                                <CustomDatePicker
                                    value={poCompletionDate}
                                    onChange={setPoCompletionDate}
                                    disabled={isBusy}
                                    minDate={poStartDate || new Date()}
                                />
                            </div>
                            <div>
                                <Label required>Is BG Applicable</Label>
                                <div className="relative">
                                    <select
                                        className={`${inputCls} appearance-none pr-10`}
                                        value={bgApplicable}
                                        onChange={(e) => setBgApplicable(e.target.value)}
                                        disabled={isBusy}
                                    >
                                        <option value="">-Please Select-</option>
                                        {BG_APPLICABLE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                                    </select>
                                    <SelectIcon loading={false} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Section 3: PO Value ──────────────────────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <SectionHeader
                        icon={IndianRupee}
                        title="PO Value"
                        subtitle="Basic value, GST and total"
                    />
                    <div className="p-6 md:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <Label required>PO Value (₹)</Label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0.00"
                                    className={inputCls}
                                    value={poValue}
                                    onChange={handleNumericChange(setPoValue)}
                                    disabled={isBusy}
                                />
                            </div>
                            <div>
                                <Label>GST (₹)</Label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0.00"
                                    className={inputCls}
                                    value={gst}
                                    onChange={handleNumericChange(setGst)}
                                    disabled={isBusy}
                                />
                            </div>
                            <div>
                                <Label>Total (₹)</Label>
                                <input
                                    type="text"
                                    readOnly
                                    value={formatIndianCurrency(total)}
                                    className={`${inputCls} font-bold`}
                                />
                            </div>
                        </div>
                        {total > 0 && (
                            <p className="text-xs text-indigo-500 dark:text-indigo-400 mt-2 italic">
                                {getAmountDisplay(total).words}
                            </p>
                        )}
                    </div>
                </div>

                {/* ── Section 4: Payment Term ───────────────────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <SectionHeader
                        icon={Wallet}
                        title="Payment Term"
                        subtitle="Mobilisation advance, RA bill and remarks"
                    />
                    <div className="p-6 md:p-8">
                        <div className={`grid grid-cols-1 md:grid-cols-${mobAdvance === '1' ? '4' : '3'} gap-6 mb-6`}>
                            <div>
                                <Label required>Mobilisation Advance</Label>
                                <div className="relative">
                                    <select
                                        className={`${inputCls} appearance-none pr-10`}
                                        value={mobAdvance}
                                        onChange={(e) => setMobAdvance(e.target.value)}
                                        disabled={isBusy}
                                    >
                                        <option value="">-Please Select-</option>
                                        {MOB_ADVANCE_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                                    </select>
                                    <SelectIcon loading={false} />
                                </div>
                            </div>
                            <div>
                                <Label required>RA Bill</Label>
                                <input
                                    type="text"
                                    className={inputCls}
                                    placeholder="Enter RA Bill"
                                    value={raBill}
                                    onChange={(e) => setRaBill(e.target.value)}
                                    disabled={isBusy}
                                />
                            </div>
                            <div>
                                <Label>RA Bill Dues</Label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    className={inputCls}
                                    placeholder="0.00"
                                    value={raBillDues}
                                    onChange={handleNumericChange(setRaBillDues)}
                                    disabled={isBusy}
                                />
                            </div>
                            {mobAdvance === '1' && (
                                <div>
                                    <Label required>Advance Settlement</Label>
                                    <input
                                        type="text"
                                        className={inputCls}
                                        placeholder="Enter Advance Settlement"
                                        value={advSettle}
                                        onChange={(e) => setAdvSettle(e.target.value)}
                                        disabled={isBusy}
                                    />
                                </div>
                            )}
                        </div>

                        <Label required>User Remarks</Label>
                        <textarea
                            rows={3}
                            placeholder="Enter remarks…"
                            value={userRemarks}
                            onChange={(e) => setUserRemarks(e.target.value)}
                            disabled={isBusy}
                            className={`${inputCls} resize-none`}
                        />
                    </div>
                </div>

                {/* ── Summary ───────────────────────────────────────────── */}
                {showSummary && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <SectionHeader icon={CheckCircle} title="PO Summary" subtitle="Review before submitting" />
                        <div className="p-6 md:p-8">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                                {[
                                    { label: 'Cost Center', value: `${costCenter} — ${selectedCCName}` },
                                    { label: 'Client', value: selectedClientName },
                                    { label: 'Sub-Client', value: selectedSubClientName },
                                    { label: 'PO No', value: pono },
                                    { label: 'Start Date', value: formatDateForAPI(poStartDate) },
                                    { label: 'Completion Date', value: formatDateForAPI(poCompletionDate) },
                                    { label: 'BG Applicable', value: bgApplicable },
                                    { label: 'PO Value', value: `₹ ${formatIndianCurrency(parseFloat(poValue) || 0)}` },
                                    { label: 'GST', value: `₹ ${formatIndianCurrency(parseFloat(gst) || 0)}` },
                                    { label: 'Total', value: `₹ ${formatIndianCurrency(total)}` },
                                    { label: 'Mobilisation Advance', value: MOB_ADVANCE_OPTIONS.find(o => o.id === mobAdvance)?.label },
                                    { label: 'RA Bill', value: raBill },
                                    { label: 'RA Bill Dues', value: raBillDues || '—' },
                                    ...(mobAdvance === '1' ? [{ label: 'Advance Settlement', value: advSettle }] : []),
                                    { label: 'Created By', value: userName },
                                    { label: 'Remarks', value: userRemarks, wide: true },
                                ].map(({ label, value, wide }) => (
                                    <div key={label} className={`bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 ${wide ? 'md:col-span-3' : ''}`}>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                                        <p className="font-semibold text-gray-800 dark:text-gray-100 break-words">{value ?? '—'}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Action Buttons ────────────────────────────────────── */}
                <div className="flex items-center justify-end gap-3 pb-6">
                    <button
                        onClick={handleFullReset}
                        disabled={isBusy}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                    >
                        <RotateCcw className="h-4 w-4" /> Reset
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                        {saveLoading
                            ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
                            : <><Send className="h-4 w-4" /> Submit</>}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default AddNewClientPO;
